import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { Search, Tag, ArrowRight } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const SuggestChipsSchema = z.object({
  chips: z.array(
    z.object({
      label: z.string(),
      action: z.string().optional(),
      icon: z.enum(["search", "tag", "arrow"]).optional(),
    }),
  ),
});

type SuggestChipsData = z.infer<typeof SuggestChipsSchema>;

/* ────────────────────── Icon helper ────────────────────── */

const ICON_MAP = {
  search: <Search className="size-3" />,
  tag: <Tag className="size-3" />,
  arrow: <ArrowRight className="size-3" />,
} as const;

/* ────────────────────── Component ────────────────────── */

export function SuggestChips({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<SuggestChipsData>) {
  const { chips } = widget.data;

  function handleChipClick(action?: string) {
    const target = action ?? widget.deepLink;
    if (target && onDeepLink) {
      onDeepLink(target);
    }
  }

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {chips.map((chip, idx) => (
        <button
          key={idx}
          onClick={() => handleChipClick(chip.action)}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border border-stroke-subtle bg-surface-raised px-3 py-1.5",
            "text-[12px] font-medium text-text-primary",
            "transition-colors hover:border-accent/40 hover:bg-accent/5 hover:text-text-brand",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30",
            (chip.action || widget.deepLink) && onDeepLink
              ? "cursor-pointer"
              : "cursor-default",
          )}
        >
          {chip.icon && ICON_MAP[chip.icon as keyof typeof ICON_MAP]}
          {chip.label}
        </button>
      ))}
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

const CHIP_WIDTHS = ["w-16", "w-20", "w-14", "w-24", "w-[4.5rem]", "w-12"];

export function SuggestChipsSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {CHIP_WIDTHS.map((w, i) => (
        <Skeleton
          key={i}
          className={cn("h-7 rounded-full", w)}
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const suggestChipsWidget = defineWidget<SuggestChipsData>({
  type: WIDGET_TYPES.SUGGEST_CHIPS,
  label: "추천 키워드 칩",
  schema: SuggestChipsSchema,
  component: SuggestChips,
  skeleton: SuggestChipsSkeleton,
  defaultLayout: "compact",
  maxHeight: 60,
  aiHint: "추천 검색어, 연관 키워드 칩 목록 (클릭 시 해당 페이지로 이동)",
});

registerWidget(suggestChipsWidget);
